// IEA Gear View: the door opens on the gear front. Pure: state in, HTML out.
// Words: IEA_GEAR_VIEW_WORDS.md (approved 2026-10-16). Mockup: MOCKUP_IEA_GEAR_VIEW.html v8.
// Step 1: the MCC front in Collect. Step 2: panels and switchboards. Words 9: panel circles.
// Step 3: the same door in Map and Photos, the mode switch live with the door open, and the
// section 8 main rules (MB is the main breaker; MLO never draws a main face).
// Door actions use data-gv. Cards inside reuse Collect's data-cl controls; photos reuse data-ph and data-pv.

import { tagName, tagsForAsset } from "./survey-tags";
import { esc } from "./training-screens";
import { cardFor, circuitsOf, finished, hasValue, isMccAsset, modelReady, recOf, rowRec, rowsOf, rowStatus, shown, spacesOf, wireLinkOf, type Site, type Status } from "./survey-collect";
import { bucketCard, feedBelow, mainCard, tableHtml, type CollectPanelState } from "./survey-collect-screens";
import { gearKindOf, lineupOf, MAIN_NAME, panelSlots, panelWalk, placeWords, sectionSpan, slotWords, walkOrder, type GearKind, type GearLineup, type PanelSlot } from "./survey-gear";
import { assetPhotosHtml, cameraSvg, rowPhotoCount, rowPhotoHtml, type PhotosPanelState } from "./survey-photos-screens";
import { codeOf, fullId, shortName, placeOf } from "./survey-map";

export type GearMode = "map" | "collect" | "photos";

export interface GearState {
  assetId: string;
  view: "front" | "table";
}

/** What the door needs besides Collect's panel state. */
export interface GearExtra {
  mode: GearMode;
  /** Only admins see Photos (storage rule 3). */
  photosOk: boolean;
  src: PhotosPanelState["src"];
}

/** The pick that opens the housing's main, not a position. */
export const MAIN_PICK = "__main";
/** The pick that opens the panel size question. */
export const SIZE_PICK = "__size";

/** One space is 62 px tall. A section holds 6X. */
const UNIT = 62;

interface Bucket {
  point: string;
  status: Status;
  /** Every value the model needs is recorded: the green light. */
  ready: boolean;
  line: string;
  spare: boolean;
  feedsGear: boolean;
  mapped: boolean;
}

/** Everything one drawing of the door reads. */
interface View {
  site: Site;
  assetId: string;
  sel: string | null;
  mode: GearMode;
}

export function gearKindFor(site: Pick<Site, "reg">, assetId: string): GearKind | null {
  const a = site.reg.byId.get(assetId);
  return a ? gearKindOf(codeOf(a)) : null;
}

/** What a bucket or breaker feeds, from the map: gear, a load, or nothing mapped yet. */
function bucketOf(site: Site, assetId: string, point: string): Bucket {
  const row = rowsOf(site, assetId).find((r) => r.point === point);
  const mcc = isMccAsset(site, assetId);
  const status = row ? rowStatus(rowRec(site, assetId, row), mcc) : "none";
  const ready = row ? modelReady(rowRec(site, assetId, row), mcc) : false;
  const below = feedBelow(site, assetId, point);
  if (!below) return { point, status, ready, line: "Not mapped", spare: false, feedsGear: false, mapped: false };
  if (below.kind === "asset") {
    const a = site.reg.byId.get(below.ref);
    return { point, status, ready, line: `Feeds ${a ? shortName(a) : below.ref}`, spare: false, feedsGear: true, mapped: true };
  }
  const l = site.links.find((x) => x.id === below.ref);
  const note = (l?.notes ?? "").trim();
  if (/^(spare|space)$/i.test(note)) return { point, status, ready, line: note.charAt(0).toUpperCase() + note.slice(1).toLowerCase(), spare: true, feedsGear: false, mapped: true };
  const hp = l?.endLoadHp ? `${l.endLoadHp} HP` : "";
  const what = [hp, note].filter(Boolean).join(" ") || (l?.endLoadType === "Motor" ? "Motor" : "Load");
  return { point, status, ready, line: what, spare: false, feedsGear: false, mapped: true };
}

const stCls = (st: Status) => (st !== "none" ? ` ${st}` : "");

/** Collect status as a lamp (Robert 2026-10-19): gray Not Started, yellow Started, green Done.
 *  Started moved from red to yellow on 2026-10-20: red means overduty only.
 *  Gold is kept for the one you are on. */
const LAMP: Record<Status, [string, string]> = { none: ["off", "Not Started"], started: ["start", "Started"], missing: ["miss", "Missing Data"], done: ["go", "Done"] };

/** The door's legend plate (Robert 2026-10-20; Missing Data added 2026-10-30, SURVEY_DONE_LOOKED_C1_WORDS.md item 7): four lights with their words, only the current one lit.
 *  Reads like the engraved legend under real pilot lights. */
function legendLights(st: Status): string {
  const cur = LAMP[st][0];
  return `<span class="lg ${cur}" title="${LAMP[st][1]}">${(["none", "started", "missing", "done"] as Status[]).map((k) => `<span class="${LAMP[k][0]}${LAMP[k][0] === cur ? " on" : ""}"><i></i><b>${LAMP[k][1]}</b></span>`).join("")}</span>`;
}

/** The corner dot on a breaker, feeder or main in Collect: same three colors as the MCC lamps. */
function stDot(v: View, st: Status): string {
  if (v.mode !== "collect") return "";
  const [c, w] = LAMP[st];
  return `<i class="gv-st ${c}" title="${w}"></i>`;
}

/** The class a cell takes in the mode: Collect status, Map gold for gear it feeds, nothing in Photos. */
function modeCls(v: View, b: Bucket): string {
  if (v.mode === "collect") return stCls(b.status);
  if (v.mode === "map" && b.feedsGear) return " out";
  return "";
}

/** The camera count on a cell in Photos mode. */
function camMark(v: View, point: string): string {
  if (v.mode !== "photos") return "";
  const n = rowPhotoCount(v.site, v.assetId, point);
  if (n === null) return '<i class="gv-cam none">Not opened</i>';
  return `<i class="gv-cam${n ? "" : " zero"}">${cameraSvg(n > 0)}${n}</i>`;
}

/** The housing's main: done when every main field it asks for is filled. */
function mainStatus(site: Site, assetId: string): Status {
  const r = recOf(site, assetId);
  const fields = cardFor(site, assetId).fields.filter((f) => f.name.startsWith("Main") && shown(f, r));
  const filled = fields.filter((f) => hasValue(r, f));
  if (!filled.length) return "none";
  return fields.every((f) => !f.req || hasValue(r, f)) ? "done" : "started";
}

const mainOrMlo = (site: Site, assetId: string) => String(recOf(site, assetId).f["Main or MLO"] ?? "");

/** "MAIN LUG 225 A", "MAIN BREAKER 400 A", or "MAIN · Tap to set" from the housing card and the MB row. */
function mainWords(site: Site, assetId: string): [string, string] {
  const f = recOf(site, assetId).f;
  const amps = (v: unknown) => (v !== undefined && v !== "" ? `${v} A` : "");
  if (f["Main or MLO"] === "MLO") return ["MAIN LUG", amps(f["Housing Ampere Rating"]) || "Amps not set"];
  const mb = rowsOf(site, assetId).find((r) => r.point === MAIN_NAME);
  const mbAmps = mb ? amps(rowRec(site, assetId, mb).f["Rating (A)"]) : "";
  if (f["Main or MLO"] === "Main" || mb) return ["MAIN BREAKER", mbAmps || amps(f["Main Trip Amps"]) || amps(f["Main Frame Amps"]) || amps(f["Housing Ampere Rating"]) || "Amps not set"];
  return ["MAIN", "Tap to set"];
}

/** The pick the main opens: the MB row when the registry has one, else the housing's main fields. */
function mainPickOf(site: Site, assetId: string): string {
  return rowsOf(site, assetId).some((r) => r.point === MAIN_NAME) ? MAIN_NAME : MAIN_PICK;
}

function mainSt(v: View): Status {
  const pick = mainPickOf(v.site, v.assetId);
  return pick === MAIN_NAME ? bucketOf(v.site, v.assetId, MAIN_NAME).status : mainStatus(v.site, v.assetId);
}

function mainCls(v: View): string {
  return v.mode === "collect" ? stCls(mainSt(v)) : "";
}

// ---- MCC ----

function door(v: View, b: Bucket, half: boolean, height: number, main = false): string {
  const cls = `gv-door${half ? " half" : ""}${main ? " main" : ""}${b.spare ? " spare" : ""}${modeCls(v, b)}${v.sel === b.point ? " sel" : ""}`;
  const line = main ? `Main breaker${b.line === "Not mapped" ? "" : ` · ${b.line}`}` : b.line;
  // The position name sits beside the lamps; the nameplate carries only the circuit description,
  // blank until it is mapped, like the real engraved plate (Robert 2026-10-20).
  const plate = line === "Not mapped" ? "" : line;
  return `<button type="button" class="${cls}" style="height:${height}px" data-gv="pick" data-gv-v="${esc(b.point)}" aria-label="${esc(`${b.point} ${line}`)}"><i class="hdl"></i>${b.spare ? "" : legendLights(b.status)}<b class="pt">${esc(b.point)}</b>${camMark(v, b.point)}<span class="np"><span>${esc(plate)}</span></span></button>`;
}

/** A plate naming the housing's main when there is no MB position: MAIN LUG or MAIN BREAKER. */
function mainPlate(v: View): string {
  const [w, a] = mainWords(v.site, v.assetId);
  return `<button type="button" class="gv-lug${mainCls(v)}${v.sel === MAIN_PICK ? " sel" : ""}" data-gv="pick" data-gv-v="${MAIN_PICK}">${stDot(v, mainSt(v))}<b>${esc(w)}</b><span>${esc(a)}</span></button>`;
}

/** The MCC lineup: tall sections side by side, a wireway along the bottom, a vertical wireway beside each. */
export function mccFront(v: View, l: GearLineup, hasMb: boolean): string {
  const mlo = mainOrMlo(v.site, v.assetId);
  let h = '<div class="gv-lu"><div class="gv-row">';
  l.sections.forEach((sc, si) => {
    let body = "";
    if (si === 0) {
      // Section 8: an MB position is the main breaker door; MLO or a set Main without MB gets a plate.
      if (hasMb) {
        body += door(v, bucketOf(v.site, v.assetId, MAIN_NAME), false, UNIT * 2 - 3, true);
      } else if (mlo === "MLO" || mlo === "Main") body += mainPlate(v);
    }
    for (const sp of sc.spaces) {
      const ht = UNIT - 3;
      body += sp.points.length > 1 ? `<div class="gv-pair">${sp.points.map((p) => door(v, bucketOf(v.site, v.assetId, p), true, ht)).join("")}</div>` : door(v, bucketOf(v.site, v.assetId, sp.points[0]!), false, ht);
    }
    // Robert 2026-10-16: no blank plates. The rest of the section stays plain gray, the same height
    // across the lineup, so it never reads as built for more buckets.
    h += `<div class="gv-sec"><div class="gv-col"><div class="gv-tag"><b>${sc.letter ? `SECTION ${esc(sc.letter)}` : "POSITIONS"}</b></div><div class="gv-units" style="min-height:${l.height * UNIT + 8}px">${body}</div><div class="gv-ww"></div></div><div class="gv-vww"></div></div>`;
  });
  return `${h}</div></div>`;
}

// ---- Switchboard (words 4b) ----

/** Which main the switchboard draws (words 8): none for MLO; MB when there is one; the housing's main
 *  when Main is set; until Main or MLO is set, the first position unless the map shows it feeding gear. */
function swbdMain(v: View, l: GearLineup, hasMb: boolean): { kind: "lug" } | { kind: "point"; point: string } | { kind: "housing" } {
  const mlo = mainOrMlo(v.site, v.assetId);
  if (mlo === "MLO") return { kind: "lug" };
  if (hasMb) return { kind: "point", point: MAIN_NAME };
  if (mlo === "Main") return { kind: "housing" };
  const first = l.named ? l.sections[0]?.spaces[0]?.points : undefined;
  if (first && first.length === 1 && !bucketOf(v.site, v.assetId, first[0]!).feedsGear) return { kind: "point", point: first[0]! };
  return { kind: "housing" };
}

function mainFace(v: View, pick: string, name: string, line: string, st: Status): string {
  return `<button type="button" class="gv-main${v.mode === "collect" ? stCls(st) : ""}${v.sel === pick ? " sel" : ""}" data-gv="pick" data-gv-v="${esc(pick)}" aria-label="${esc(`${name} main`)}">${stDot(v, st)}<span class="face"></span><span class="tu">TRIP</span><span class="btns"><i class="pr"></i><i class="pg"></i></span>${pick === MAIN_PICK ? "" : camMark(v, pick)}<span class="np"><b>${esc(name)}</b><span>${esc(line)}</span></span></button>`;
}

export function swbdFront(v: View, l: GearLineup, hasMb: boolean): string {
  const main = swbdMain(v, l, hasMb);
  const head = () => {
    let b = '<div class="gv-meter" aria-hidden="true"><i></i><i></i></div>';
    if (main.kind === "lug") b += mainPlate(v);
    else if (main.kind === "housing") {
      const [w, a] = mainWords(v.site, v.assetId);
      b += mainFace(v, MAIN_PICK, w, a, mainSt(v));
    } else if (main.point === MAIN_NAME) {
      const bk = bucketOf(v.site, v.assetId, MAIN_NAME);
      b += mainFace(v, MAIN_NAME, MAIN_NAME, `Main${bk.line === "Not mapped" ? "" : ` · ${bk.line}`}`, bk.status);
    }
    return b;
  };
  let h = '<div class="gv-lu"><div class="gv-row">';
  l.sections.forEach((sc, si) => {
    let body = si === 0 ? head() : "";
    for (const sp of sc.spaces) {
      for (const p of sp.points) {
        const b = bucketOf(v.site, v.assetId, p);
        if (main.kind === "point" && main.point === p) {
          body += mainFace(v, p, p, `Main${b.line === "Not mapped" ? "" : ` · ${b.line}`}`, b.status);
        } else {
          body += `<button type="button" class="gv-fd${b.spare ? " spare" : ""}${modeCls(v, b)}${v.sel === p ? " sel" : ""}" data-gv="pick" data-gv-v="${esc(p)}" aria-label="${esc(`${p} ${b.line}`)}"><i class="bk2"></i>${b.spare ? "" : stDot(v, b.status)}${camMark(v, p)}<b class="pt">${esc(p)}</b><span class="np"><span>${esc(b.line === "Not mapped" ? "" : b.line)}</span></span></button>`;
        }
      }
    }
    h += `<div class="gv-sec"><div class="gv-col sb"><div class="gv-tag"><b>${sc.letter ? `SECTION ${esc(sc.letter)}` : "POSITIONS"}</b></div><div class="gv-units" style="min-height:${l.height * UNIT}px">${body}</div><div class="gv-ww"></div></div><div class="gv-vww"></div></div>`;
  });
  if (!l.sections.length) h += `<div class="gv-sec"><div class="gv-col sb"><div class="gv-tag"><b>SECTION</b></div><div class="gv-units" style="min-height:${l.height * UNIT}px">${head()}</div><div class="gv-ww"></div></div></div>`;
  return `${h}</div></div>`;
}

// ---- Panel (words 4c) ----

/** Panel sizes offered first (words 9). */
export const PANEL_SIZES = [12, 18, 24, 30, 42, 54, 60, 84];

/** A numbered panel (or an empty one) draws circles once its size is set. Names like A2a never do. MB is the main. */
export function circlePanel(points: readonly string[]): boolean {
  return points.every((p) => p === MAIN_NAME || /^\d+$/.test(p));
}

export function panelFront(v: View, pl: { slots: PanelSlot[]; rows: number }, circuits: number | null = null, spaces: readonly string[] = []): string {
  const [w, a] = mainWords(v.site, v.assetId);
  const pick = mainPickOf(v.site, v.assetId);
  let h = `<div class="gv-can"><div class="gv-cantop"><div class="gv-stk"><i></i><i class="warn"></i></div><button type="button" class="gv-mainblk${mainCls(v)}${v.sel === pick ? " sel" : ""}" data-gv="pick" data-gv-v="${esc(pick)}">${stDot(v, mainSt(v))}<small>${esc(w)}</small><b>${esc(a)}</b>${pick === MAIN_NAME ? camMark(v, MAIN_NAME) : ""}</button><div class="gv-stk"><i></i><i class="warn"></i></div></div>`;
  const rows = Math.max(pl.rows, circuits ? Math.ceil(circuits / 2) : 0, ...spaces.map((n) => Math.ceil(Number(n) / 2)));
  if (!pl.slots.length && !circuits) return `${h}<div class="cl-note">No breakers yet. Set the panel size in Collect, or add breaker numbers on the Map.</div></div>`;
  h += `<div class="gv-slots"><div class="gv-bus" style="grid-row:1 / span ${rows}"></div>`;
  if (circuits) {
    // Every circuit not holding a breaker or a space is an empty circle to tap (words 9).
    const held = new Set<number>();
    for (const sl of pl.slots) if (/^\d+$/.test(sl.point)) for (let i = 0; i < sl.poles; i++) held.add(Number(sl.point) + 2 * i);
    for (const n of spaces) held.add(Number(n));
    for (let n = 1; n <= circuits; n++) {
      const L = n % 2 === 1;
      const row = Math.ceil(n / 2);
      if (spaces.includes(String(n))) {
        h += `<div class="gv-cnum ${L ? "L" : "R"}" style="grid-row:${row};grid-column:${L ? 2 : 6}"><span>${n}</span></div><button type="button" class="gv-brk spare${v.sel === String(n) ? " sel" : ""}" style="grid-row:${row};grid-column:${L ? 3 : 5}" data-gv="pick" data-gv-v="${n}" aria-label="Circuit ${n} space"></button><button type="button" class="gv-clbl ${L ? "L" : "R"} spare" style="grid-row:${row};grid-column:${L ? 1 : 7}" data-gv="pick" data-gv-v="${n}"><span>Space</span></button>`;
        continue;
      }
      if (held.has(n)) continue;
      h += `<div class="gv-cnum ${L ? "L" : "R"}" style="grid-row:${row};grid-column:${L ? 2 : 6}"><span>${n}</span></div><button type="button" class="gv-circ${v.sel === String(n) ? " sel" : ""}" style="grid-row:${row};grid-column:${L ? 3 : 5}" data-gv="circle" data-gv-v="${n}" aria-label="Circuit ${n}${v.mode === "collect" ? ", tap to add a breaker" : ", not recorded"}"><i></i></button>`;
    }
  }
  // Slots with nothing recorded draw empty, so the can reads like the panel. With a size set, circles do that.
  const used = new Set<string>(circuits ? Array.from({ length: rows }, (_, i) => [`L${i + 1}`, `R${i + 1}`]).flat() : []);
  for (const s of pl.slots) for (let i = 0; i < s.poles; i++) (s.side === "F" ? ["L", "R"] : [s.side]).forEach((x) => used.add(`${x}${s.row + i}`));
  for (let row = 1; row <= rows; row++)
    for (const side of ["L", "R"]) if (!used.has(`${side}${row}`)) h += `<div class="gv-slot0" style="grid-row:${row};grid-column:${side === "L" ? 3 : 5}" aria-hidden="true"></div>`;
  for (const s of pl.slots) {
    const b = bucketOf(v.site, v.assetId, s.point);
    const r = `grid-row:${s.row} / span ${s.poles};`;
    const isSel = v.sel === s.point;
    const cam = camMark(v, s.point);
    const label = `<span class="${b.feedsGear ? "gearout" : ""}">${esc(b.line)}</span>`;
    const nums = s.num
      .split("·")
      .map((n) => `<span>${esc(n)}</span>`)
      .join("");
    const brkCls = `gv-brk${s.poles > 1 ? " multi" : ""}${b.spare ? " spare" : ""}${modeCls(v, b)}${isSel ? " sel" : ""}`;
    if (s.side === "F") {
      h += `<button type="button" class="gv-clbl L${b.spare ? " spare" : ""}" style="${r}grid-column:1" data-gv="pick" data-gv-v="${esc(s.point)}">${cam}${label}</button><div class="gv-cnum L" style="${r}grid-column:2">${nums}</div><button type="button" class="${brkCls} full" style="${r}grid-column:3 / 6" data-gv="pick" data-gv-v="${esc(s.point)}" aria-label="${esc(s.point)}">${b.spare ? "" : stDot(v, b.status)}</button>`;
      continue;
    }
    const L = s.side === "L";
    h += `<button type="button" class="gv-clbl ${s.side}${b.spare ? " spare" : ""}" style="${r}grid-column:${L ? 1 : 7}" data-gv="pick" data-gv-v="${esc(s.point)}">${L ? cam + label : label + cam}</button>`;
    h += `<div class="gv-cnum ${s.side}" style="${r}grid-column:${L ? 2 : 6}">${nums}</div>`;
    h += `<button type="button" class="${brkCls}" style="${r}grid-column:${L ? 3 : 5}" data-gv="pick" data-gv-v="${esc(s.point)}" aria-label="${esc(`${s.num} ${b.line}`)}">${b.spare ? "" : stDot(v, b.status)}</button>`;
  }
  return `${h}</div></div>`;
}

// ---- The card on the left ----

function polesFor(site: Site, assetId: string): (p: string) => number {
  const rows = rowsOf(site, assetId);
  return (p) => {
    const row = rows.find((r) => r.point === p);
    const v = row ? Number(rowRec(site, assetId, row).f["Poles"]) : NaN;
    return Number.isFinite(v) ? v : 1;
  };
}

/** The panel size question (words 9). */
function sizeHtml(current: number | null): string {
  return `<div class="gv-k">Panel Size</div><div class="gv-ask">How many circuits is this panel built for?</div><div class="gv-sizes">${PANEL_SIZES.map((n) => `<button type="button" class="cl-pick${current === n ? " on" : ""}" data-gv="circuits" data-gv-v="${n}">${n}</button>`).join("")}</div>
    <div class="cl-fld"><label for="gv-circ">Other</label><div class="cl-row"><input id="gv-circ" inputmode="numeric" autocomplete="off" value="${current && !PANEL_SIZES.includes(current) ? current : ""}" placeholder="Circuits"><button type="button" class="sv-btn line sm" data-gv="circuits-other">Use This</button></div></div>
    <div class="cl-note">Count both sides. A 42-circuit panel has 21 a side. Empty circles then show every circuit to fill.</div>`;
}

/** What a breaker feeds, and whether that was seen in the field (Robert: Verified or Not Verified). */
function feedsLine(site: Site, assetId: string, point: string): string {
  const below = feedBelow(site, assetId, point);
  if (!below) return '<div class="gv-feeds">Not on the Map yet.</div>';
  const lid = wireLinkOf(below);
  const link = lid ? site.links.find((l) => l.id === lid) : undefined;
  const what = bucketOf(site, assetId, point).line;
  const ok = link?.howKnown === "Seen";
  return `<div class="gv-feeds"><b>${esc(what)}</b><span class="gv-ver${ok ? " ok" : ""}">${ok ? "Verified" : "Not Verified"}</span></div>`;
}

/** Poles, Space and Remove for a breaker on the circles. A breaker the Map uses is removed on the Map. */
function circleTools(site: Site, assetId: string, point: string): string {
  const poles = Number(recOf(site, assetId).breakers[point]?.f.Poles) || 1;
  const mapped = Boolean(site.tree.nodes.get(`p:${assetId}:${point}`));
  return `${feedsLine(site, assetId, point)}<div class="cl-sec">Poles</div><div class="cl-picks">${[1, 2, 3].map((n) => `<button type="button" class="cl-pick${poles === n ? " on" : ""}" data-gv="poles" data-gv-v="${n}">${n}</button>`).join("")}</div>
    ${mapped ? '<div class="cl-note">On the Map. To take it out, remove it on the Map.</div>' : `<div class="cl-picks"><button type="button" class="cl-pick" data-gv="slot" data-gv-v="Space">Space</button><button type="button" class="cl-pick gv-rm" data-gv="slot" data-gv-v="Removed">Remove</button></div>`}`;
}

/** Map mode: what it feeds, with the way to the Map's own flow. */
function mapCard(v: View, point: string, recorded: boolean): string {
  const b = bucketOf(v.site, v.assetId, point);
  let h = feedsLine(v.site, v.assetId, point);
  if (b.mapped) h += `<button type="button" class="sv-btn line" data-gv="feed-show" data-gv-v="${esc(point)}">Show On Diagram</button><div class="cl-note">${b.feedsGear ? "Gear it feeds stays on the diagram under this housing." : "A load stays behind the door, so the diagram stays small."} Change or remove it there.</div>`;
  else h += `<button type="button" class="sv-btn gold" data-gv="feed-add" data-gv-v="${esc(point)}">Add What It Feeds</button><div class="cl-note">${recorded ? "" : "Not recorded in Collect yet. "}Picks what it feeds the same way the Map does.</div>`;
  return h;
}

function sideHtml(v: View, s: CollectPanelState, x: GearExtra, kind: GearKind, slot: PanelSlot | undefined, circles: boolean): string {
  const { site, assetId, sel } = v;
  const name = shortName(site.reg.byId.get(assetId) ?? { assetId, location: "" });
  const word = kind === "mcc" ? "Bucket" : "Breaker";
  const circuits = circuitsOf(site, assetId);
  const rows = rowsOf(site, assetId);
  if (sel === SIZE_PICK) return sizeHtml(circuits);
  const isSpace = circles && sel !== null && spacesOf(site, assetId).includes(sel);
  if (isSpace) {
    const place = `<div class="gv-k">Space</div><div class="gv-place">Circuit ${esc(sel!)} · ${Number(sel) % 2 ? "left" : "right"} side, row ${Math.ceil(Number(sel) / 2)}</div><div class="gv-big">${esc(name)} · ${esc(sel!)}</div><div class="cl-note">No breaker installed in this opening.</div>`;
    return v.mode === "collect" ? `${place}<div class="cl-picks"><button type="button" class="cl-pick" data-gv="slot" data-gv-v="Breaker">Add Breaker Here</button><button type="button" class="cl-pick gv-rm" data-gv="slot" data-gv-v="Removed">Remove</button></div>` : place;
  }
  if (sel === MAIN_PICK) {
    const [w, a] = mainWords(site, assetId);
    const top = `<div class="gv-k">Main</div><div class="gv-place">On the housing card</div><div class="gv-big">${esc(name)} · ${esc(w === "MAIN" ? "Main" : `${w.charAt(0)}${w.slice(1).toLowerCase()}`)}</div>`;
    if (v.mode === "collect") return `${top}${mainCard(site, s, assetId)}`;
    return `${top}<div class="gv-feeds"><b>${esc(a)}</b></div><div class="cl-note">${v.mode === "photos" ? "The main's photos are the housing photos." : "Main or MLO is set on the housing card in Collect."}</div>`;
  }
  if (!sel) {
    if (v.mode === "map") {
      const mapped = rows.filter((r) => bucketOf(site, assetId, r.point).mapped).length;
      return `<div class="gv-k">${word}s · ${mapped} of ${rows.length} mapped</div><div class="cl-note">Tap a ${word.toLowerCase()} to see what it feeds. Blue nameplates feed other gear, which stays on the diagram. A blank nameplate is not mapped yet.</div>`;
    }
    if (v.mode === "photos") {
      const counts = rows.map((r) => rowPhotoCount(site, assetId, r.point));
      const shot = counts.filter((n) => n !== null && n > 0).length;
      const need = counts.filter((n) => n !== null).length;
      return `<div class="gv-k">${esc(name)} photos</div>${assetPhotosHtml(site, assetId, { src: x.src, mark: s.mark })}<div class="gv-gap"></div><div class="gv-k">${word}s · ${shot} of ${need} photographed</div><div class="cl-note">Tap a ${word.toLowerCase()} to see its photo. The big view is one tap away.</div>`;
    }
    const mcc = kind === "mcc";
    const done = rows.filter((r) => finished(rowStatus(rowRec(site, assetId, r), mcc))).length;
    const run = kind === "panel" ? "Photo Run goes down the left side, then the right." : "Photo Run goes section by section, top to bottom, the way you walk the lineup.";
    const size = circles && !circuits ? `${sizeHtml(null)}<div class="gv-gap"></div>` : circles && circuits ? `<button type="button" class="cl-fold1" data-gv="pick" data-gv-v="${SIZE_PICK}"><b>${circuits} circuits</b><span>Panel size · tap to change</span></button><div class="cl-note">Tap an empty circle to add a breaker. Set its poles on its card.</div>` : "";
    return `${size}<div class="gv-k">${word}s · ${done} of ${rows.length} done</div><div class="cl-note">Tap a ${word.toLowerCase()}. Its card opens here: Opened, Device, Rating, Setting${mcc ? ", Starter" : ""}, Photo.${kind === "mcc" ? "" : " Tap the main for the main's fields."}</div>
      ${rows.length ? `<button type="button" class="sv-btn gold" data-cl="photo-run" data-cl-a="${esc(assetId)}">Photo Run</button><div class="cl-note">${run}</div>` : ""}`;
  }
  const recorded = rows.some((r) => r.point === sel);
  const place = slot ? slotWords(slot) : recorded || sel === MAIN_NAME ? placeWords(sel) : `Circuit ${sel} · ${Number(sel) % 2 ? "left" : "right"} side, row ${Math.ceil(Number(sel) / 2)}`;
  const head = `<div class="gv-k">${sel === MAIN_NAME ? "Main Breaker" : word}</div><div class="gv-place">${esc(place)}</div><div class="gv-big">${esc(name)} · ${esc(slot && slot.num !== slot.point ? slot.num : sel)}</div>`;
  if (v.mode === "map") return head + mapCard(v, sel, recorded);
  if (v.mode === "photos") {
    if (!recorded) return `${head}<div class="cl-note">Not recorded yet. Tap its circle in Collect first.</div>`;
    // Collect is the switch in the door header: no second button for it (Robert 2026-10-20).
    return `${head}<div class="cl-sec">Photos</div>${rowPhotoHtml(site, assetId, sel, { src: x.src, mark: s.mark })}`;
  }
  return head + nextBucket(v, kind) + (circles && sel !== MAIN_NAME ? circleTools(site, assetId, sel) : "") + bucketCard(site, s, assetId, sel);
}

/** Next Bucket (Robert 2026-10-19): the next one not done, in the order Photo Run walks. Wraps. */
function nextBucket(v: View, kind: GearKind): string {
  const rows = rowsOf(v.site, v.assetId);
  const pts = rows.map((r) => r.point);
  const order = kind === "panel" ? [...(pts.includes(MAIN_NAME) ? [MAIN_NAME] : []), ...panelWalk(panelSlots(pts, polesFor(v.site, v.assetId)).slots)] : walkOrder(pts);
  const mcc = kind === "mcc";
  const done = (p: string) => {
    const row = rows.find((r) => r.point === p);
    return !row || finished(rowStatus(rowRec(v.site, v.assetId, row), mcc));
  };
  const at = v.sel ? order.indexOf(v.sel) : -1;
  for (let i = 1; i < order.length; i++) {
    const p = order[(at + i + order.length) % order.length]!;
    if (p !== v.sel && !done(p)) return `<button type="button" class="sv-btn line gv-next" data-gv="pick" data-gv-v="${esc(p)}">Next ${mcc ? "Bucket" : "Breaker"} · ${esc(p)}</button>`;
  }
  return `<div class="gv-alldone">Every ${mcc ? "bucket" : "breaker"} is done.</div>`;
}

// ---- The door ----

/** The door, open: header with the mode switch (and Front and Table in Collect), the card left, the gear right. */
export function gearOverlay(site: Site, s: CollectPanelState, g: GearState, x: GearExtra = { mode: "collect", photosOk: true, src: () => ({ state: "loading" }) }): string {
  const kind = gearKindFor(site, g.assetId) ?? "mcc";
  const mode = x.mode === "photos" && !x.photosOk ? "map" : x.mode;
  const a = site.reg.byId.get(g.assetId);
  const rows = rowsOf(site, g.assetId);
  const points = rows.map((r) => r.point);
  const hasMb = points.includes(MAIN_NAME);
  const l = lineupOf(points);
  const pl = kind === "panel" ? panelSlots(points, polesFor(site, g.assetId)) : null;
  const circles = kind === "panel" && circlePanel(points);
  const circuits = circles ? circuitsOf(site, g.assetId) : null;
  const spaces = circles ? spacesOf(site, g.assetId) : [];
  const node = site.tree.nodes.get(`a:${g.assetId}`);
  const id = node ? fullId(s.siteNumber, node) : g.assetId;
  const room = a ? placeOf(a).room : null;
  const count = kind === "mcc" ? `${rows.length} buckets` : `${rows.length} breakers`;
  const tag = tagsForAsset(s.tags ?? [], g.assetId).map((t) => tagName(t, s.prefixes)).join(" + ");
  const sub = [id, tag, a?.equipmentType || (kind === "mcc" ? "Motor Control Center" : kind === "panel" ? "Panel" : "Switchboard"), room, count, kind === "panel" ? null : rows.length ? sectionSpan(l) : null].filter(Boolean).join(" · ");
  const circleSel = (p: string) => circles && circuits !== null && /^\d+$/.test(p) && Number(p) <= circuits;
  const valid = (p: string) => (p === MAIN_PICK ? true : p === SIZE_PICK ? circles && mode === "collect" : points.includes(p) || spaces.includes(p) || (mode !== "collect" && circleSel(p)));
  const sel = s.row && valid(s.row) ? s.row : null;
  const view = mode === "collect" ? g.view : "front";
  const v: View = { site, assetId: g.assetId, sel, mode };
  const modes = `<span class="sv-seg gv-modes"><button type="button" data-mp="mode-map" class="${mode === "map" ? "on" : ""}">Map</button><button type="button" data-mp="mode-collect" class="${mode === "collect" ? "on" : ""}">Collect</button>${x.photosOk ? `<button type="button" data-mp="mode-photos" class="${mode === "photos" ? "on" : ""}">Photos</button>` : ""}</span>`;
  const tabs = mode === "collect" ? `<span class="sv-seg gv-views"><button type="button" data-gv="view" data-gv-v="front" class="${view === "front" ? "on" : ""}">Front</button><button type="button" data-gv="view" data-gv-v="table" class="${view === "table" ? "on" : ""}">Table</button></span>` : "";
  const head = `<div class="gv-hd"><div class="gv-nm"><b>${esc(a ? shortName(a) : g.assetId)}</b><small>${esc(sub)}</small></div>${modes}${tabs}<button type="button" class="gv-done" data-gv="done">Done</button></div>`;
  const rule =
    kind === "panel"
      ? "Panels: two sides. Odd circuits left, even right, top to bottom. Names like A2a and A2b: a left, b right."
      : l.named || !rows.length
        ? "Names from the registry: letter is the section, number is the space top to bottom, a and b share a space."
        : "These names do not follow the lineup rule, so they draw in order.";
  const lamps = '<span><i class="gv-led off"></i>Not Started</span><span><i class="gv-led start"></i>Started</span><span><i class="gv-led miss"></i>Missing Data</span><span><i class="gv-led go"></i>Done</span>';
  const key =
    mode === "collect"
      ? lamps
      : mode === "map"
        ? '<span><i class="gv-sw out"></i>Feeds other gear, on the diagram</span><span><i class="gv-sw"></i>A load, behind the door</span><span><i class="gv-sw blank"></i>Blank: not mapped yet</span><span><i class="gv-sw spare"></i>Spare or space</span>'
        : `<span>${cameraSvg(true)} photos taken for that ${kind === "mcc" ? "bucket" : "breaker"}</span>`;
  const lights = kind === "mcc" && mode !== "collect" ? lamps : "";
  const legend = `<div class="gv-legend">${key}${lights}<span>${rule}</span></div>`;
  const front = kind === "panel" ? panelFront(v, pl!, circuits, spaces) : kind === "swbd" ? swbdFront(v, l, hasMb) : rows.length ? mccFront(v, l, hasMb) : '<div class="cl-note">No buckets in the registry for this MCC yet.</div>';
  const slot = pl && sel ? pl.slots.find((p) => p.point === sel) : undefined;
  const body =
    view === "table"
      ? `<div class="gv-body gv-tablebody"><div class="gv-tablewrap" id="gv-table">${tableHtml(site, s, g.assetId)}</div></div>`
      : `<div class="gv-body"><div class="gv-front" id="gv-front">${front}</div><div class="gv-side" id="gv-side">${sideHtml(v, s, x, kind, slot, circles)}</div></div>`;
  return `<div class="gv-bg gv-${mode}" id="gv-bg"><div class="gv-cab" role="dialog" aria-label="${esc(a ? shortName(a) : g.assetId)} gear front">${head}${body}${view === "front" ? legend : ""}</div></div>`;
}
